/**
 * AI Usage API client with Zod validation
 *
 * Responses from the backend are snake_case and match the Pydantic schemas
 * in backend/features/ai_gateway/schemas.py. We validate with Zod and infer
 * TypeScript types directly from the response structure (snake_case).
 */

import { z } from 'zod';
import { api } from './api';

/**
 * `cost_usd` is `Decimal | None` in Pydantic, and Pydantic serialises a
 * Decimal to JSON as a STRING: the wire value is `"1.234567"`, not
 * `1.234567`. A plain `z.number()` threw on every priced row (see the
 * backend-generated payloads in `__tests__/aiUsageService.test.ts`).
 *
 * NOT `z.coerce.number()`: that maps `null` to `0`, rendering "$0.00" for
 * local-model usage. `null` = "no cost applies", `0` = "a price applied
 * and came to zero"; the explicit null branch keeps them distinct.
 */
const nullableCost = z
  .union([z.number(), z.string()])
  .nullable()
  .transform((value) => (value === null ? null : Number(value)));

// Response schemas with Zod validation - exact backend format (snake_case)
const SummaryRowSchema = z.object({
  user: z.string().nullable().optional(),
  // Readable names for the id dimensions. Optional as well as nullable:
  // a backend older than this field simply omits them, and the UI falls
  // back to the id rather than failing to parse the whole response.
  user_label: z.string().nullable().optional(),
  project: z.string().nullable().optional(),
  project_label: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  provider: z.string().nullable().optional(),
  client: z.string().nullable().optional(),
  tokens_in: z.number(),
  tokens_out: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  requests: z.number(),
  cost_usd: nullableCost,
});

const SummaryResponseSchema = z.object({
  rows: z.array(SummaryRowSchema),
  scope_degraded: z.boolean(),
});

const TimeseriesPointSchema = z.object({
  day: z.string(), // ISO date string from backend
  tokens_in: z.number(),
  tokens_out: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  requests: z.number(),
  cost_usd: nullableCost,
});

const TimeseriesResponseSchema = z.object({
  points: z.array(TimeseriesPointSchema),
  scope_degraded: z.boolean(),
});

const MeResponseSchema = z.object({
  rows: z.array(SummaryRowSchema),
});

// TypeScript types inferred from schemas (snake_case from backend)
export type SummaryRow = z.infer<typeof SummaryRowSchema>;
export type SummaryResponse = z.infer<typeof SummaryResponseSchema>;
export type TimeseriesPoint = z.infer<typeof TimeseriesPointSchema>;
export type TimeseriesResponse = z.infer<typeof TimeseriesResponseSchema>;
export type MeResponse = z.infer<typeof MeResponseSchema>;

export interface SummaryQuery {
  start?: string; // ISO date
  end?: string; // ISO date
  groupBy?: string[];
  project_id?: string;
}

export interface TimeseriesQuery {
  start?: string; // ISO date
  end?: string; // ISO date
  project_id?: string;
}

const USAGE_PATH = '/ai/usage';

/**
 * Build query parameters from SummaryQuery.
 * - group_by is sent as a repeated query param (FastAPI array handling)
 * - Empty/undefined filters are omitted
 * - Dates are optional (backend defaults to 30-day window)
 */
export function buildSummaryParams(query: SummaryQuery): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};

  if (query.start) params.start = query.start;
  if (query.end) params.end = query.end;
  if (query.groupBy && query.groupBy.length > 0) params.group_by = query.groupBy;
  if (query.project_id) params.project_id = query.project_id;

  return params;
}

/**
 * Build query parameters for timeseries query
 */
export function buildTimeseriesParams(query: TimeseriesQuery): Record<string, string> {
  const params: Record<string, string> = {};

  if (query.start) params.start = query.start;
  if (query.end) params.end = query.end;
  if (query.project_id) params.project_id = query.project_id;

  return params;
}

/**
 * Fetch usage summary
 */
export async function fetchSummary(query: SummaryQuery): Promise<SummaryResponse> {
  const response = await api.get<unknown>(`${USAGE_PATH}/summary`, {
    params: buildSummaryParams(query),
  });
  return SummaryResponseSchema.parse(response.data);
}

/**
 * Fetch usage timeseries
 */
export async function fetchTimeseries(query: TimeseriesQuery): Promise<TimeseriesResponse> {
  const response = await api.get<unknown>(`${USAGE_PATH}/timeseries`, {
    params: buildTimeseriesParams(query),
  });
  return TimeseriesResponseSchema.parse(response.data);
}

/**
 * Fetch the current user's own usage
 */
export async function fetchMyUsage(query: Omit<SummaryQuery, 'project_id'>): Promise<MeResponse> {
  const params: Record<string, string | string[]> = {};

  if (query.start) params.start = query.start;
  if (query.end) params.end = query.end;
  if (query.groupBy && query.groupBy.length > 0) params.group_by = query.groupBy;

  const response = await api.get<unknown>(`${USAGE_PATH}/me`, { params });
  return MeResponseSchema.parse(response.data);
}
